'use strict';

const restoreWindowScrollTop = require('../utils/fixedHeaderScrollOffset').restoreWindowScrollTop;

/**
 * Opens a PDF URL in a new browser tab.
 *
 * @param {string} url - PDF URL.
 */
function openPdfInNewTab(url) {
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Builds an embed URL that fits the full PDF page inside an iframe preview.
 *
 * @param {string} url - Original PDF URL.
 * @returns {string} URL with viewer fragment parameters.
 */
function getPdfEmbedUrl(url) {
    if (!url) return '';

    if (url.indexOf('#') >= 0) return url;

    return url + '#toolbar=0&navpanes=0&scrollbar=0&view=Fit';
}

/**
 * Reads currently rendered gallery media from desktop markup.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 * @returns {Array<Object>} Product media data (images, optional video, optional PDF).
 */
function getMedia($gallery) {
    const media = [];

    $gallery.find('.b-pdp-product-images-desktop .b-pdp-product-images-item').each(function () {
        const $item = $(this);
        const type = $item.data('mediaType') || 'image';
        const label = $item.attr('aria-label') || '';

        if (type === 'video' || type === 'pdf') {
            media.push({
                type: type,
                url: $item.data(type === 'video' ? 'videoUrl' : 'pdfUrl') || '',
                alt: label,
                title: label
            });

            return;
        }

        const $img = $item.find('.b-pdp-product-images-image').first();

        if (!$img.length) return;

        media.push({
            type: 'image',
            url: $img.attr('src'),
            alt: $img.attr('alt') || '',
            title: $img.attr('title') || ''
        });
    });

    return media;
}

/**
 * Wraps a slide index for infinite modal navigation (e.g. prev on first → last).
 *
 * @param {number} index - Target index.
 * @param {number} length - Total media count.
 * @returns {number} Wrapped index within 0 … length - 1.
 */
function wrapIndex(index, length) {
    if (!length) return 0;

    return ((index % length) + length) % length;
}

/**
 * Scrolls the modal filmstrip so the active thumbnail stays visible.
 *
 * @param {jQuery} $modal - Modal jQuery element.
 * @param {number} index - Active media index.
 */
function scrollToActiveThumbnail($modal, index) {
    const $thumb = $modal.find('.b-pdp-product-images-modal-thumbnail').eq(index);

    if (!$thumb.length) return;

    $thumb.get(0).scrollIntoView({
        block: 'nearest',
        inline: 'center'
    });
}

/**
 * Stops and removes any modal video player currently in the stage.
 *
 * @param {jQuery} $stageMedia - Modal stage media container.
 */
function clearModalVideo($stageMedia) {
    const $video = $stageMedia.find('.b-pdp-product-images-modal-video');

    if ($video.length) {
        const videoEl = $video.get(0);

        if (videoEl) {
            videoEl.pause();
            videoEl.removeAttribute('src');
            videoEl.load();
        }

        $video.remove();
    }

    $stageMedia.find('.b-pdp-product-images-play').remove();
    $stageMedia.removeClass('m-video m-playing');
}

/**
 * Removes any modal PDF iframe currently in the stage.
 *
 * @param {jQuery} $stageMedia - Modal stage media container.
 */
function clearModalPdf($stageMedia) {
    $stageMedia.find('.b-pdp-product-images-modal-pdf').remove();
    $stageMedia.removeClass('m-pdf').removeData('pdfUrl');
}

/**
 * Updates the fullscreen modal to show the selected media item.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 * @param {number} index - Media index to display.
 */
function setModalImage($gallery, index) {
    const mediaItems = $gallery.data('modalImages') || [];

    if (!mediaItems.length) return;

    const safeIndex = wrapIndex(index, mediaItems.length);
    const item = mediaItems[safeIndex];
    const $modal = $gallery.find('.b-pdp-product-images-modal');
    const $stageMedia = $modal.find('.b-pdp-product-images-modal-stage-media');
    const hasMultipleMedia = mediaItems.length > 1;

    $gallery.data('modalIndex', safeIndex);
    clearModalVideo($stageMedia);
    clearModalPdf($stageMedia);

    if (item.type === 'video' || item.type === 'pdf') {
        $stageMedia.find('.b-pdp-product-images-modal-image').hide();
    }

    if (item.type === 'video') {
        $stageMedia.addClass('m-video');

        const $video = $('<video>', {
            class: 'b-pdp-product-images-modal-video',
            src: item.url + '#t=0.001',
            muted: true,
            playsinline: true,
            preload: 'metadata'
        });
        const $play = $('<span>', {
            class: 'b-pdp-product-images-play',
            'aria-hidden': 'true'
        });

        $stageMedia.append($video, $play);
        $modal.find('.ai-image-label').show();
    } else if (item.type === 'pdf') {
        $stageMedia.addClass('m-pdf').data('pdfUrl', item.url);

        const $iframe = $('<iframe>', {
            class: 'b-pdp-product-images-modal-pdf',
            src: getPdfEmbedUrl(item.url),
            title: item.alt || '',
            tabindex: '-1',
            'aria-hidden': 'true'
        });

        $stageMedia.append($iframe);
        $modal.find('.ai-image-label').show();
    } else {
        let $img = $stageMedia.find('.b-pdp-product-images-modal-image');

        if (!$img.length) {
            $img = $('<img>', { class: 'b-pdp-product-images-modal-image' });
            $stageMedia.prepend($img);
        }

        $img.show().attr({ src: item.url, alt: item.alt });
        $modal.find('.ai-image-label').toggle(safeIndex > 0);
    }

    $modal.find('.b-pdp-product-images-modal-thumbnail').removeClass('m-active').eq(safeIndex).addClass('m-active');
    $modal.find('.b-pdp-product-images-modal-nav, .b-pdp-product-images-modal-filmstrip-wrap').toggle(hasMultipleMedia);

    if (hasMultipleMedia) scrollToActiveThumbnail($modal, safeIndex);
}

/**
 * Pauses any inline gallery video players so they do not overlap modal media.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 */
function pauseInlineGalleryVideos($gallery) {
    $gallery.find('.b-pdp-product-images-item.m-video video').each(function () {
        if (typeof this.pause === 'function') this.pause();
    });
}

/**
 * Opens the fullscreen gallery modal at the selected media item.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 * @param {number} index - Media index to open.
 */
function openModal($gallery, index) {
    const mediaItems = getMedia($gallery);
    const $modal = $gallery.find('.b-pdp-product-images-modal');
    const $filmstrip = $modal.find('.b-pdp-product-images-modal-filmstrip');

    if (!mediaItems.length) return;

    const startIndex = wrapIndex(index, mediaItems.length);

    pauseInlineGalleryVideos($gallery);

    $gallery.data('modalImages', mediaItems);
    $filmstrip.empty();

    mediaItems.forEach(function (item, mediaIndex) {
        const typeClass = item.type === 'image' ? '' : (' m-' + item.type);
        const $button = $('<button>', {
            type: 'button',
            class: 'b-pdp-product-images-modal-thumbnail'
                + typeClass
                + (mediaIndex === startIndex ? ' m-active' : ''),
            'data-index': mediaIndex
        });

        if (item.type === 'video') {
            const $preview = $('<video>', {
                class: 'b-pdp-product-images-modal-thumbnail-video',
                src: item.url + '#t=0.001',
                muted: true,
                playsinline: true,
                preload: 'metadata'
            });
            const $play = $('<span>', {
                class: 'b-pdp-product-images-play',
                'aria-hidden': 'true'
            });

            $filmstrip.append($button.append($preview, $play));
        } else if (item.type === 'pdf') {
            const $preview = $('<iframe>', {
                class: 'b-pdp-product-images-modal-thumbnail-pdf',
                src: getPdfEmbedUrl(item.url),
                title: item.alt || '',
                tabindex: '-1',
                'aria-hidden': 'true'
            });

            $filmstrip.append($button.append($preview));
        } else {
            const $img = $('<img>', { src: item.url, alt: item.alt || '' });

            $filmstrip.append($button.append($img));
        }
    });

    setModalImage($gallery, startIndex);
    $modal.one('shown.bs.modal', function () {
        scrollToActiveThumbnail($modal, $gallery.data('modalIndex') || 0);
    });
    $modal.one('hidden.bs.modal', function () {
        const $stageMedia = $modal.find('.b-pdp-product-images-modal-stage-media');

        clearModalVideo($stageMedia);
        clearModalPdf($stageMedia);
    });
    $modal.modal('show');
}

/**
 * Applies per-image fit mode based on intrinsic aspect ratio.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 */
function setImageFitMode($gallery) {
    $gallery.find('img.b-pdp-product-images-image').each(function () {
        const img = this;
        const apply = function () {
            if (!img.naturalWidth || !img.naturalHeight) return;

            const ratio = img.naturalWidth / img.naturalHeight;

            img.classList.toggle('m-cover', ratio > 0.9 && ratio < 1.2);
        };

        if (img.complete) {
            apply();
        } else {
            img.addEventListener('load', apply, { once: true });
        }
    });
}

/**
 * Starts video playback inside a gallery/modal video container (same element).
 *
 * @param {jQuery} $item - Video container (gallery tile or modal stage media).
 */
function playGalleryVideoInline($item) {
    if (!$item || !$item.length || $item.hasClass('m-playing')) return;

    const video = $item.find('video').get(0);

    if (!video) return;

    $item.addClass('m-playing');
    $item.find('.b-pdp-product-images-play').remove();

    video.controls = true;
    video.muted = false;
    video.removeAttribute('aria-hidden');
    video.setAttribute('controlslist', 'nodownload noremoteplayback noplaybackrate');
    video.setAttribute('disablepictureinpicture', '');
    video.play();
}

/**
 * Binds delegated gallery events once per product gallery instance.
 *
 * @param {jQuery} $gallery - Gallery root jQuery element.
 */
function bindGallery($gallery) {
    if ($gallery.data('pdpGalleryBound')) return;

    $gallery.data('pdpGalleryBound', true);
    setImageFitMode($gallery);

    const stepModal = function (delta) {
        setModalImage($gallery, ($gallery.data('modalIndex') || 0) + delta);
    };

    $gallery.on('click', '.b-pdp-product-images-toggle', function () {
        const willCollapse = $gallery.hasClass('m-expanded');

        if (!willCollapse) {
            $gallery.data('expandScrollTop', window.scrollY);
            $gallery.addClass('m-expanded');

            return;
        }

        const savedScrollTop = $gallery.data('expandScrollTop');

        $gallery.removeClass('m-expanded');
        $gallery.removeData('expandScrollTop');
        restoreWindowScrollTop(savedScrollTop);
    });

    $gallery.on('click', '.b-pdp-product-images-desktop .b-pdp-product-images-item:not(.m-video):not(.m-pdf)', function () {
        openModal($gallery, Number(this.dataset.index));
    });

    $gallery.on('click', '.b-pdp-product-images-item.m-pdf, .b-pdp-product-images-modal-stage-media.m-pdf', function (event) {
        event.preventDefault();
        openPdfInNewTab($(this).data('pdfUrl'));
    });

    $gallery.on('keydown', '.b-pdp-product-images-item.m-pdf', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        openPdfInNewTab($(this).data('pdfUrl'));
    });

    $gallery.on('click', '.b-pdp-product-images-item.m-video:not(.m-playing), .b-pdp-product-images-modal-stage-media.m-video:not(.m-playing)', function () {
        playGalleryVideoInline($(this));
    });

    $gallery.on('keydown', '.b-pdp-product-images-desktop .b-pdp-product-images-item.m-video:not(.m-playing)', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        playGalleryVideoInline($(this));
    });

    $gallery.on('click', '.b-pdp-product-images-modal-control.m-prev', function () {
        stepModal(-1);
    });

    $gallery.on('click', '.b-pdp-product-images-modal-control.m-next', function () {
        stepModal(1);
    });

    $gallery.on('click', '.b-pdp-product-images-modal-thumbnail', function () {
        setModalImage($gallery, Number(this.dataset.index));
    });

    $gallery.on('keydown', '.b-pdp-product-images-modal', function (event) {
        if (event.key === 'ArrowLeft') stepModal(-1);
        if (event.key === 'ArrowRight') stepModal(1);
    });
}

/**
 * Replaces gallery markup with server-rendered HTML after variation change.
 *
 * @param {string} html - Rendered gallery HTML from Product-Variation response.
 * @param {jQuery} $productContainer - Product detail container.
 */
function replaceGallery(html, $productContainer) {
    const $gallery = $productContainer && $productContainer.length ? $productContainer.find('.b-pdp-product-images') : null;

    if (!$gallery || !$gallery.length || !html) return;

    $gallery.find('.b-pdp-product-images-modal').modal('hide');
    $gallery.replaceWith(html);
    bindGallery($productContainer.find('.b-pdp-product-images'));
}

/**
 * Initializes the product image gallery on the page.
 */
function init() {
    const $gallery = $('.b-pdp-product-images');

    if ($gallery.length) bindGallery($gallery);
}

module.exports = {
    init: init,
    replaceGallery: replaceGallery
};
